import { useQuery } from '@tanstack/react-query'
import { TECHNICIAN_CARD_AUTHORITY, onTechnicianCard } from '@fieldops/shared'
import { useEffect, useState } from 'react'
import {
  CommitInput,
  readCard,
  SaveState,
  UndoNotice,
  useCardWriter,
  useLicenceWrites,
  useTicketWriter,
  useUndo,
  type LicenceWrites,
  type TechnicianCard,
} from '../../components/licences/kit.js'
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  SkeletonRows,
  SwitchTrack,
} from '../../components/ui.js'
import { cn } from '../../lib/cn.js'
import { fileUrl, uploadFile } from '../../lib/files.js'
import {
  attentionLabel,
  dateInputValue,
  daysLeftLabel,
  needsAttention,
  standingBadge,
  standingDetail,
} from '../../lib/licences.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'

/**
 * One person's tickets.
 *
 * A row per licence type in the catalogue, not per holding, because the question
 * the screen answers is as much "what is missing" as "what is held" — and a list
 * of holdings cannot answer the first one.
 *
 * A technician card carries endorsements (decision 66), so the tickets from
 * `TECHNICIAN_CARD_AUTHORITY` are drawn as that card: one photo, one number,
 * one expiry, and a switch per endorsement. Everything else is a table edited
 * in place. Nothing has a Save button; each box writes a pause after typing.
 */

type Entry = {
  licenceType: {
    id: string
    name: string
    authority: string | null
    expires: boolean
    warnDays: number
    expectedOfFieldStaff: boolean
    archivedAt: Date | null
  }
  holding: {
    id: string
    reference: string | null
    issuedAt: Date | null
    expiresAt: Date | null
    attachmentId: string | null
    attachment: { id: string; checksum: string | null; filename: string; mimeType?: string | null } | null
    updatedAt?: Date
  } | null
  standing: string
  daysLeft: number | null
  isGap: boolean
}

export function Certifications({ userId, canEdit }: { userId: string; canEdit: boolean }) {
  const trpc = useTRPC()
  const licences = useQuery(trpc.licences.forUser.queryOptions({ userId }))
  const writes = useLicenceWrites()
  const [undo, offerUndo, dismissUndo] = useUndo()

  if (licences.error) return <ErrorState message={errorMessage(licences.error)} />

  if (licences.isPending) {
    return (
      <Card>
        <CardHeader title="Certifications" />
        <SkeletonRows rows={6} />
      </Card>
    )
  }

  const entries = licences.data as unknown as Entry[]

  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader title="Certifications" />
        <EmptyState
          title="No licence types"
          description="The catalogue is empty. Add one under Settings before recording a ticket."
        />
      </Card>
    )
  }

  const endorsements = entries.filter((entry) => onTechnicianCard(entry.licenceType))
  const others = entries.filter((entry) => !onTechnicianCard(entry.licenceType))
  const attention = entries.filter((entry) => needsAttention(entry.standing, entry.isGap)).length

  return (
    <>
      {endorsements.length > 0 ? (
        <TechnicianCardPanel
          userId={userId}
          endorsements={endorsements}
          canEdit={canEdit}
          writes={writes}
        />
      ) : null}

      <Card>
        <CardHeader
          title={endorsements.length > 0 ? 'Other tickets' : 'Certifications'}
          action={
            <span className="text-xs text-ink-500">
              {attention === 0 ? 'Nothing outstanding' : attentionLabel(attention)}
            </span>
          }
        />

        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[minmax(0,1fr)_150px_150px_96px_110px] gap-3 border-b border-ink-100 px-[var(--row-pad-x)] py-1.5 text-[11px] font-medium tracking-wide text-ink-400 uppercase">
              <span>Ticket</span>
              <span>Number</span>
              <span>Expires</span>
              <span>Days left</span>
              <span>Standing</span>
            </div>
            <ul>
              {others.map((entry) => (
                <TicketRow
                  key={entry.licenceType.id}
                  userId={userId}
                  entry={entry}
                  canEdit={canEdit}
                  writes={writes}
                  onCleared={(label, run) => offerUndo(label, run)}
                />
              ))}
            </ul>
          </div>
        </div>

        <p
          className="px-[var(--row-pad-x)] py-2 text-xs text-ink-500"
          aria-live="polite"
        >
          <SaveState writes={writes} /> Standing is worked out from today&rsquo;s date and never
          stored.
        </p>
      </Card>

      <UndoNotice undo={undo} onDone={dismissUndo} />
    </>
  )
}

/* -------------------------------------------------------------------------- */

function TechnicianCardPanel({
  userId,
  endorsements,
  canEdit,
  writes,
}: {
  userId: string
  endorsements: Entry[]
  canEdit: boolean
  writes: LicenceWrites
}) {
  const card = readCard(endorsements)
  const write = useCardWriter(userId, card, writes)
  const badge = standingBadge(card.standing.key)

  // A switch moves on the press, not when the refetch lands.
  const serverHeld = card.heldIds.join(',')
  const [shown, setShown] = useState<string[] | null>(null)
  useEffect(() => setShown(null), [serverHeld])
  const held = new Set(shown ?? card.heldIds)

  const toggle = (id: string) => {
    const next = held.has(id) ? [...held].filter((other) => other !== id) : [...held, id]
    setShown(next)
    void write({ heldIds: next })
  }

  return (
    <Card>
      <CardHeader
        title="Technician licence card"
        action={<Badge tone={badge.tone}>{badge.label}</Badge>}
      />

      <div className="grid gap-5 px-[var(--row-pad-x)] py-4 sm:grid-cols-[240px_minmax(0,1fr)]">
        <CardPhoto card={card} canEdit={canEdit} onUploaded={(id) => void write({ attachmentId: id })} />

        <div className="grid content-start gap-3 sm:grid-cols-2">
          <Field label="Card number" htmlFor="card-number">
            <CommitInput
              id="card-number"
              value={card.reference}
              placeholder={canEdit ? 'Add number' : '—'}
              disabled={!canEdit}
              className="min-h-[38px] font-mono text-[15px]"
              onCommit={(reference) => void write({ reference })}
            />
          </Field>
          <Field label="Expires" htmlFor="card-expires">
            <CommitInput
              id="card-expires"
              type="date"
              value={card.expiresAt}
              disabled={!canEdit}
              className="min-h-[38px]"
              onCommit={(expiresAt) => void write({ expiresAt })}
            />
          </Field>
          <p className="text-xs text-ink-500 sm:col-span-2">
            {cardDetail(card)} Every endorsement switched on below shares this number and date.
          </p>
        </div>
      </div>

      <div className="flex items-baseline justify-between border-t border-ink-200 bg-ink-50 px-[var(--row-pad-x)] py-2">
        <span className="text-xs font-semibold text-ink-700">Endorsements on this card</span>
        <span className="text-xs text-ink-500">
          {held.size} of {endorsements.length} switched on
        </span>
      </div>

      <ul>
        {endorsements.map((entry) => {
          const on = held.has(entry.licenceType.id)
          const standing = standingBadge(entry.standing)

          return (
            <li key={entry.licenceType.id} className="border-t border-ink-100">
              <button
                type="button"
                role="switch"
                aria-checked={on}
                disabled={!canEdit}
                onClick={() => toggle(entry.licenceType.id)}
                className="flex w-full items-center gap-3 px-[var(--row-pad-x)] py-2 text-left enabled:hover:bg-ink-50 disabled:cursor-default"
              >
                <SwitchTrack checked={on} disabled={!canEdit} />
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                  <span className={cn('text-sm font-medium', on ? 'text-ink-900' : 'text-ink-500')}>
                    {entry.licenceType.name}
                  </span>
                  {entry.licenceType.expectedOfFieldStaff ? <Badge>Expected</Badge> : null}
                  {entry.licenceType.archivedAt ? <Badge>Retired</Badge> : null}
                </span>
                <Badge tone={standing.tone}>{standing.label}</Badge>
              </button>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

function cardDetail(card: TechnicianCard): string {
  if (card.heldIds.length === 0) {
    return `Issued by ${TECHNICIAN_CARD_AUTHORITY}. Switch on an endorsement and the number and date save with it.`
  }

  const sentence = standingDetail({
    standing: card.standing.key,
    daysLeft: card.standing.daysLeft,
    licenceType: { authority: null, expires: true },
  })
  const disagree = card.disagree ? ' The endorsements had different numbers or dates; the next change evens them out.' : ''

  return `${sentence.endsWith('.') ? sentence : `${sentence}.`}${disagree}`
}

/**
 * The photographed card.
 *
 * The tile is the file picker, so replacing a photo is the same press as adding
 * one. `scope: 'licence'` rather than a job id: a card belongs to a person, and
 * the route authorises it on `USER_MANAGE` (decision 66).
 */
function CardPhoto({
  card,
  canEdit,
  onUploaded,
}: {
  card: TechnicianCard
  canEdit: boolean
  onUploaded: (attachmentId: string) => void
}) {
  const [uploaded, setUploaded] = useState<{ id: string; checksum: string | null; mimeType: string } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const file = uploaded ?? card.attachment
  const url = file ? fileUrl(file) : null
  const isPdf = (file?.mimeType ?? '').includes('pdf')

  const pick = async (picked: File | undefined) => {
    if (!picked) return
    setProblem(null)
    setUploading(true)

    try {
      const result = await uploadFile(picked, { scope: 'licence', kind: 'PHOTO' })
      setUploaded(result)
      onUploaded(result.id)
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'That file did not upload.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label
        className={cn(
          'relative block h-[140px] w-full max-w-[240px] overflow-hidden rounded-lg ring-1 ring-inset ring-ink-200',
          'bg-[repeating-linear-gradient(135deg,var(--color-ink-100)_0_8px,var(--color-ink-200)_8px_9px)]',
          canEdit && 'cursor-pointer hover:ring-ink-400',
        )}
      >
        {canEdit ? (
          <input
            type="file"
            accept="image/*,application/pdf"
            aria-label="Photo of the card"
            disabled={uploading}
            onChange={(event) => void pick(event.target.files?.[0])}
            className="absolute size-px opacity-0"
          />
        ) : null}

        {uploading ? (
          <span className="absolute inset-0 grid place-items-center font-mono text-[11px] text-ink-500">
            Uploading…
          </span>
        ) : url && !isPdf ? (
          <span
            role="img"
            aria-label="Card on file"
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url("${url}")` }}
          />
        ) : file ? (
          <span className="absolute inset-0 grid place-items-center font-mono text-[11px] text-ink-600">
            {isPdf ? 'PDF on file' : 'Processing…'}
          </span>
        ) : (
          <span className="absolute inset-0 grid place-items-center font-mono text-[11px] text-ink-500">
            {canEdit ? '+ photo of the card' : 'No photo'}
          </span>
        )}
      </label>

      {problem ? (
        <p className="text-xs text-red-700">{problem}</p>
      ) : url ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          View the card on file
        </a>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function TicketRow({
  userId,
  entry,
  canEdit,
  writes,
  onCleared,
}: {
  userId: string
  entry: Entry
  canEdit: boolean
  writes: LicenceWrites
  onCleared: (label: string, undo: () => void) => void
}) {
  const write = useTicketWriter(userId, entry, writes)
  const badge = standingBadge(entry.standing)
  const { licenceType, holding } = entry

  const clear = () => {
    if (!holding) return
    const before = holding
    writes.remove.mutate(
      { id: before.id },
      {
        onSuccess: () =>
          onCleared(`Cleared ${licenceType.name}`, () =>
            writes.record.mutate({
              userId,
              licenceTypeId: licenceType.id,
              reference: before.reference,
              issuedAt: before.issuedAt,
              expiresAt: before.expiresAt,
              attachmentId: before.attachmentId,
            }),
          ),
      },
    )
  }

  const ghost =
    'min-h-[30px] bg-transparent px-2 text-xs ring-0 hover:ring-1 hover:ring-ink-300 hover:bg-white focus:bg-white disabled:bg-transparent disabled:hover:ring-0'

  return (
    <li className="grid grid-cols-[minmax(0,1fr)_150px_150px_96px_110px] items-center gap-3 border-b border-ink-100 px-[var(--row-pad-x)] py-1.5 hover:bg-ink-50">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-ink-900">{licenceType.name}</span>
          {licenceType.expectedOfFieldStaff ? <Badge>Expected</Badge> : null}
          {licenceType.archivedAt ? <Badge>Retired</Badge> : null}
        </p>
        <p className="mt-px text-xs text-ink-500">{standingDetail(entry)}</p>
      </div>

      {licenceType.expires ? (
        <>
          <CommitInput
            aria-label={`${licenceType.name} number`}
            value={holding?.reference ?? ''}
            placeholder={canEdit ? 'Add number' : '—'}
            disabled={!canEdit}
            className={cn(ghost, 'font-mono text-ink-700')}
            onCommit={(reference) => void write({ reference })}
          />
          <CommitInput
            aria-label={`${licenceType.name} expires`}
            type="date"
            value={dateInputValue(holding?.expiresAt)}
            disabled={!canEdit}
            className={cn(ghost, holding?.expiresAt ? 'text-ink-700' : 'text-ink-400')}
            onCommit={(expiresAt) => void write({ expiresAt })}
          />
          <span className="text-xs tabular-nums text-ink-500">{daysLeftLabel(entry.daysLeft)}</span>
        </>
      ) : (
        <>
          <SignOffButton
            signedOff={holding !== null}
            disabled={!canEdit}
            onClick={() =>
              holding
                ? writes.remove.mutate({ id: holding.id })
                : writes.record.mutate({ userId, licenceTypeId: licenceType.id, reference: null })
            }
          />
          <span className="text-xs text-ink-400">No expiry</span>
        </>
      )}

      <span className="flex items-center justify-between gap-1">
        <Badge tone={badge.tone}>{badge.label}</Badge>
        {canEdit && holding ? (
          <button
            type="button"
            aria-label={`Clear ${licenceType.name}`}
            title="Clear"
            onClick={clear}
            className="grid size-6 place-items-center rounded text-[15px] leading-none text-ink-400 hover:bg-ink-100 hover:text-red-700"
          >
            ×
          </button>
        ) : null}
      </span>
    </li>
  )
}

/** The one control a ticket that does not lapse needs. Spans number and expiry. */
function SignOffButton({
  signedOff,
  disabled,
  onClick,
}: {
  signedOff: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={signedOff}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'col-span-2 min-h-7 justify-self-start rounded-md px-2.5 text-xs font-medium ring-1 ring-inset disabled:cursor-default',
        signedOff
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-white text-ink-700 ring-ink-300 enabled:hover:bg-ink-50',
      )}
    >
      {signedOff ? '✓ Signed off' : 'Mark signed off'}
    </button>
  )
}
